import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Download, Package, Users, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOrderByCode } from "@/integrations/supabase/orders";
import { getMedidas } from "@/integrations/supabase/medidas";
import { toast } from "sonner";
import PrintLayout from "@/components/PrintLayout";
import { useAuth } from "@/components/AuthProvider";
import { getArquivos, ArquivoData } from "@/integrations/supabase/arquivos";

// Função para formatar moeda
const formatCurrency = (value: number) => {
  try {
    // Verificar se o valor é válido
    if (value === null || value === undefined || isNaN(value)) {
      console.warn("formatCurrency: valor inválido:", value);
      return "R$ 0,00";
    }
    
    const numValue = Number(value);
    if (isNaN(numValue)) {
      console.warn("formatCurrency: conversão falhou:", value);
      return "R$ 0,00";
    }
    
    const formatted = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue);
    
    console.log("formatCurrency: valor formatado:", formatted);
    return formatted;
  } catch (error) {
    console.error("formatCurrency error:", error, "value:", value);
    return "R$ 0,00";
  }
};

export default function OrdemProducao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const [arquivos, setArquivos] = useState<ArquivoData[]>([]);

  // Buscar dados do pedido
  const { data: order, isLoading, error } = useQuery({
    queryKey: ["order", id],
    queryFn: () => getOrderByCode(id!),
    enabled: !!id,
  });

  // Buscar medidas do cliente
  const { data: medidas = [] } = useQuery({
    queryKey: ["medidas", empresa?.id],
    queryFn: () => getMedidas(empresa?.id || ''),
    enabled: !!empresa?.id && !!order?.customer_name,
  });

  // Carregar arquivos quando o pedido é carregado
  useEffect(() => {
    if (!id) return;

    const loadArquivos = async () => {
      try {
        const arquivosCarregados = await getArquivos('orders', id);
        setArquivos(arquivosCarregados);
      } catch (error) {
        console.error("Erro ao carregar arquivos:", error);
      }
    };

    loadArquivos();
  }, [id]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando ordem de produção...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    console.error("Order is null or undefined");
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Pedido não encontrado</h2>
          <p className="text-gray-600 mb-6">O pedido solicitado não foi encontrado ou não existe.</p>
          <Button onClick={() => navigate('/pedidos')} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Voltar para Pedidos
          </Button>
        </div>
      </div>
    );
  }

  // Criar objeto seguro com valores padrão
  const safeOrder = {
    id: order.id || '',
    code: order.code || 'N/A',
    type: order.type || 'N/A',
    status: order.status || 'N/A',
    customer_name: order.customer_name || 'N/A',
    customer_phone: order.customer_phone || 'N/A',
    customer_email: order.customer_email || 'N/A',
    description: order.description || 'N/A',
    value: order.value !== null && order.value !== undefined && !isNaN(Number(order.value)) ? Number(order.value) : 0,
    paid: order.paid !== null && order.paid !== undefined && !isNaN(Number(order.paid)) ? Number(order.paid) : 0,
    delivery_date: order.delivery_date || 'N/A',
    observations: order.observations || 'Sem observações',
    file_url: order.file_url || null,
    created_at: order.created_at || new Date().toISOString(),
    updated_at: order.updated_at || new Date().toISOString(),
    personalizations: Array.isArray(order.personalizations) ? order.personalizations : [],
    items: (order as any).items || [], // ✅ Adicionar itens reais do pedido
  };

  console.log("=== ORDEM DE PRODUÇÃO - DADOS SEGUROS ===");
  console.log("Order original:", order);
  console.log("Order keys:", Object.keys(order || {}));
  console.log("Order value field:", order?.value, "type:", typeof order?.value);
  console.log("Order paid field:", order?.paid, "type:", typeof order?.paid);
  console.log("Safe Order:", safeOrder);
  console.log("Safe Order value:", safeOrder.value, "type:", typeof safeOrder.value);
  console.log("Safe Order paid:", safeOrder.paid, "type:", typeof safeOrder.paid);
  console.log("formatCurrency test:", formatCurrency(safeOrder.value));
  console.log("formatCurrency test paid:", formatCurrency(safeOrder.paid));

  // Função para gerar PDF (reutilizável)
  const generatePDF = () => {
    console.log("=== GERANDO PDF ORDEM DE PRODUÇÃO ===");
    console.log("Safe Order:", safeOrder);
    console.log("Medidas disponíveis:", medidas);
    
    // Filtrar medidas do cliente atual
    const medidasCliente = medidas.filter(medida => 
      medida.cliente_nome.toLowerCase().includes(safeOrder.customer_name.toLowerCase())
    );
    console.log("Medidas do cliente:", medidasCliente);
    
    // Gerar HTML completo do PDF
    const pdfHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ordem de Produção - ${safeOrder.code}</title>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              line-height: 1.4;
              color: #2c3e50;
              background: white;
              padding: 10px;
              font-size: 11px;
            }
            .container { max-width: 210mm; margin: 0 auto; }
            .header {
              margin-bottom: 12px;
              border-bottom: 2px solid #2c3e50;
              padding-bottom: 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .header h1 { font-size: 16px; font-weight: bold; margin: 0; color: #2c3e50; }
            .header-info { text-align: right; font-size: 10px; color: #555; }
            .header-info div { margin: 2px 0; }
            .section {
              margin-bottom: 10px;
              border: 1px solid #bdc3c7;
              border-radius: 3px;
            }
            .section-header {
              background: #34495e;
              color: white;
              padding: 5px 8px;
              font-weight: bold;
              font-size: 11px;
            }
            .section-content {
              padding: 8px;
              background: #ecf0f1;
            }
            .info-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 8px;
              margin-bottom: 0;
            }
            .info-item {
              display: flex;
              flex-direction: column;
            }
            .info-label {
              font-size: 9px;
              color: #555;
              font-weight: bold;
              text-transform: uppercase;
              margin-bottom: 2px;
            }
            .info-value {
              font-size: 11px;
              color: #2c3e50;
              font-weight: 600;
            }
            .personal-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 0;
              font-size: 10px;
            }
            .personal-table th,
            .personal-table td {
              border: 1px solid #bdc3c7;
              padding: 4px 6px;
              text-align: left;
            }
            .personal-table th {
              background: #34495e;
              color: white;
              text-transform: uppercase;
              font-weight: bold;
              font-size: 9px;
            }
            .personal-table td:first-child {
              font-weight: 600;
            }
            .checklist {
              background: white;
              padding: 6px 8px;
            }
            .checklist ul {
              list-style: none;
              padding: 0;
              margin: 0;
            }
            .checklist li {
              margin-bottom: 3px;
              display: flex;
              align-items: center;
              font-size: 10px;
            }
            .checklist li span {
              color: #27ae60;
              margin-right: 6px;
              font-weight: bold;
              font-size: 12px;
            }
            .row-compact { display: flex; gap: 12px; margin-bottom: 6px; }
            .col-compact { flex: 1; }
            @media print {
              body { padding: 0; }
              .container { max-width: none; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>ORDEM DE PRODUÇÃO</h1>
              <div class="header-info">
                <div><strong>Cód:</strong> ${safeOrder.code}</div>
                <div><strong>Data:</strong> ${new Date().toLocaleDateString('pt-BR')}</div>
              </div>
            </div>

            <div class="section">
              <div class="section-header">INFORMAÇÕES DO PEDIDO E CLIENTE</div>
              <div class="section-content">
                <div class="info-grid">
                  <div class="info-item">
                    <div class="info-label">Código Pedido</div>
                    <div class="info-value">${safeOrder.code}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Cliente</div>
                    <div class="info-value">${safeOrder.customer_name}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Telefone</div>
                    <div class="info-value">${safeOrder.customer_phone}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Entrega</div>
                    <div class="info-value">${safeOrder.delivery_date}</div>
                  </div>
                </div>
              </div>
            </div>

            ${safeOrder.personalizations.length ? `
            <div class="section">
              <div class="section-header">PERSONALIZAÇÕES INDIVIDUAIS</div>
              <div class="section-content">
                <table class="personal-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tamanho</th>
                      <th>Qtd</th>
                      <th>Observações</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${safeOrder.personalizations.map((item: any) => `
                      <tr>
                        <td>${item.person_name || ''}</td>
                        <td>${item.size || '—'}</td>
                        <td style="text-align: center;">${item.quantity ?? 1}</td>
                        <td>${item.notes || ''}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              </div>
            </div>
            ` : ''}

            ${(safeOrder.items && safeOrder.items.length > 0) || safeOrder.description ? `
            <div class="section">
              <div class="section-header">ITENS DO PEDIDO</div>
              <div class="section-content">
                <table class="personal-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style="width: 15%;">Qtd</th>
                      <th style="width: 25%;">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${safeOrder.items && safeOrder.items.length > 0
                      ? safeOrder.items.map((item: any) => `
                      <tr>
                        <td>${item.description || 'Produto/Serviço'}</td>
                        <td style="text-align: center;">${item.quantity || 1}</td>
                        <td style="text-align: right;">${formatCurrency((item.unit_value || 0) * (item.quantity || 1))}</td>
                      </tr>
                    `).join('')
                      : safeOrder.description.split('\\n').filter((line: string) => line.trim()).map((line: string, idx: number) => `
                      <tr>
                        <td>${line.trim()}</td>
                        <td style="text-align: center;">—</td>
                        <td style="text-align: right;">—</td>
                      </tr>
                    `).join('')
                    }
                    <tr style="background: #34495e; color: white; font-weight: bold;">
                      <td style="border-color: white;">TOTAL</td>
                      <td style="border-color: white;"></td>
                      <td style="text-align: right; border-color: white;">${formatCurrency(safeOrder.value)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            ` : ''}

            ${safeOrder.items && safeOrder.items.some((item: any) => item.especificacoes && Object.keys(item.especificacoes).length > 0) ? `
            <div class="section">
              <div class="section-header">ESPECIFICAÇÕES TÉCNICAS DOS PRODUTOS</div>
              <div class="section-content">
                ${safeOrder.items.filter((item: any) => item.especificacoes && Object.keys(item.especificacoes).length > 0).map((item: any) => `
                  <div style="margin-bottom: 12px; padding: 8px; background: #f5f5f5; border-left: 3px solid #34495e; border-radius: 2px;">
                    <div style="font-weight: bold; margin-bottom: 4px;">
                      ${item.description || 'Produto'}
                      ${item.category ? ` <span style="font-weight: normal; color: #7f8c8d;">(${item.category})</span>` : ''}
                    </div>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 10px;">
                      ${Object.entries(item.especificacoes).map(([key, value]: [string, any]) => `
                        <div><strong>${key}:</strong> ${value}</div>
                      `).join('')}
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
            ` : ''}

            ${arquivos.length > 0 ? `
            <div class="section">
              <div class="section-header">ARTES / ARQUIVOS DO PEDIDO</div>
              <div class="section-content">
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px;">
                  ${arquivos.map((arquivo: ArquivoData) => `
                    <div style="text-align: center; padding: 8px; border: 1px solid #bdc3c7; border-radius: 4px; background: #fafafa;">
                      <div style="font-weight: bold; font-size: 9px; margin-bottom: 4px; color: #2c3e50;">${arquivo.posicao}</div>
                      ${arquivo.url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) ? `
                        <img src="${arquivo.url}" alt="${arquivo.nome}" style="max-width: 100%; max-height: 100px; border: 1px solid #bdc3c7; border-radius: 2px; margin-bottom: 4px;" />
                      ` : `
                        <div style="background: #e8e8e8; padding: 10px; border-radius: 2px; margin-bottom: 4px; font-size: 9px;">📄</div>
                      `}
                      <div style="font-size: 8px; color: #7f8c8d; word-break: break-word;">
                        ${arquivo.nome}
                      </div>
                      ${arquivo.descricao ? `
                        <div style="font-size: 8px; color: #555; margin-top: 2px;">
                          ${arquivo.descricao}
                        </div>
                      ` : ''}
                    </div>
                  `).join('')}
                </div>
              </div>
            </div>
            ` : safeOrder.file_url ? `
            <div class="section">
              <div class="section-header">ARTE/DESIGN DO PEDIDO</div>
              <div class="section-content">
                <div style="text-align: center; padding: 8px 0;">
                  <img src="${safeOrder.file_url}" alt="Arte do Pedido" style="max-width: 100%; max-height: 180px; border: 1px solid #bdc3c7; border-radius: 4px;" />
                  <p style="margin-top: 6px; font-size: 9px; color: #7f8c8d;">Arte/Design anexado ao pedido</p>
                </div>
              </div>
            </div>
            ` : ''}

            <div class="section">
              <div class="section-header">CHECKLIST E DESCRIÇÃO COMPLETA</div>
              <div style="display: flex; gap: 12px;">
                <div style="flex: 1;">
                  <div style="font-weight: bold; color: #34495e; margin-bottom: 6px; font-size: 10px;">VERIFICAÇÕES</div>
                  <div class="checklist">
                    <ul style="columns: 2; column-gap: 8px;">
                      <li><span>☐</span> Verificar especificações técnicas</li>
                      <li><span>☐</span> Confirmar materiais necessários</li>
                      <li><span>☐</span> Executar produção conforme padrões</li>
                      <li><span>☐</span> Realizar controle de qualidade</li>
                      <li><span>☐</span> Empacotar e preparar para entrega</li>
                    </ul>
                  </div>
                </div>
                <div style="flex: 1;">
                  <div style="font-weight: bold; color: #34495e; margin-bottom: 6px; font-size: 10px;">DESCRIÇÃO COMPLETA</div>
                  <div style="border: 1px solid #bdc3c7; padding: 6px 8px; background: #fafafa; font-size: 9px; max-height: 90px; overflow-y: auto; line-height: 1.4;">
                    ${safeOrder.description || 'Sem descrição'}
                  </div>
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-header">OBSERVAÇÕES / ANOTAÇÕES</div>
              <div class="section-content">
                <div style="border: 1px solid #bdc3c7; padding: 8px; min-height: 80px; background: #fafafa; line-height: 20px;">
                  <div style="height: 18px; border-bottom: 1px solid #d1d5db; margin-bottom: 2px;"></div>
                  <div style="height: 18px; border-bottom: 1px solid #d1d5db; margin-bottom: 2px;"></div>
                  <div style="height: 18px; border-bottom: 1px solid #d1d5db; margin-bottom: 2px;"></div>
                  <div style="height: 18px; border-bottom: 1px solid #d1d5db; margin-bottom: 2px;"></div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    // Abrir nova janela e imprimir
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(pdfHtml);
      newWindow.document.close();
      
      // Aguardar o conteúdo carregar e então imprimir
      newWindow.onload = () => {
        newWindow.print();
      };
    }
  };

  // Função para imprimir
  const handlePrint = () => {
    generatePDF();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/pedidos')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ordem de Produção</h1>
            <p className="text-gray-600">Código: {safeOrder.code}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrint}
            className="flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Imprimir
          </Button>
          <Button
            onClick={generatePDF}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Baixar PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações do Pedido */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Informações do Pedido
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Código</label>
                <p className="text-lg font-semibold">{safeOrder.code}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Tipo</label>
                <p className="text-lg font-semibold">{safeOrder.type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <Badge variant="secondary" className="mt-1">
                  {safeOrder.status}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Data de Entrega</label>
                <p className="text-lg font-semibold">{safeOrder.delivery_date}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Informações do Cliente */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Informações do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Nome</label>
              <p className="text-lg font-semibold">{safeOrder.customer_name}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Telefone</label>
                <p className="text-lg font-semibold">{safeOrder.customer_phone}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-lg font-semibold">{safeOrder.customer_email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detalhes do Pedido */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Detalhes do Pedido</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Descrição</label>
              <p className="text-lg font-semibold mt-1">{safeOrder.description}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Valor Total</label>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(safeOrder.value)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Valor Pago</label>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(safeOrder.paid)}</p>
              </div>
            </div>

            {safeOrder.observations && safeOrder.observations !== 'Sem observações' && (
              <div>
                <label className="text-sm font-medium text-gray-500">Observações</label>
                <p className="text-lg font-semibold mt-1">{safeOrder.observations}</p>
              </div>
            )}

            {safeOrder.file_url && (
              <div>
                <label className="text-sm font-medium text-gray-500">Logo/Arte do Pedido</label>
                <div className="mt-2">
                  <img 
                    src={safeOrder.file_url} 
                    alt="Logo/Arte do Pedido" 
                    className="max-w-xs max-h-48 object-contain border rounded-lg"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {safeOrder.personalizations.length > 0 && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Personalizações Individuais
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-blue-50 text-blue-700">
                      <th className="px-4 py-2 text-left font-semibold uppercase tracking-wide">Nome / Identificação</th>
                      <th className="px-4 py-2 text-center font-semibold uppercase tracking-wide">Tamanho</th>
                      <th className="px-4 py-2 text-center font-semibold uppercase tracking-wide">Quantidade</th>
                      <th className="px-4 py-2 text-left font-semibold uppercase tracking-wide">Observações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeOrder.personalizations.map((item: any) => (
                      <tr key={item.id ?? `${item.person_name}-${item.size}`} className="border-b last:border-0">
                        <td className="px-4 py-2 font-medium text-gray-800">{item.person_name || "—"}</td>
                        <td className="px-4 py-2 text-center text-gray-600">{item.size || "—"}</td>
                        <td className="px-4 py-2 text-center text-gray-600">{item.quantity ?? 1}</td>
                        <td className="px-4 py-2 text-gray-600">{item.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Medidas do Cliente */}
        {medidas.filter(medida => 
          medida.cliente_nome.toLowerCase().includes(safeOrder.customer_name.toLowerCase())
        ).length > 0 && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Medidas do Cliente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {medidas
                  .filter(medida => 
                    medida.cliente_nome.toLowerCase().includes(safeOrder.customer_name.toLowerCase())
                  )
                  .map((medida) => (
                    <div key={medida.id} className="p-4 bg-gray-50 rounded-lg border">
                      <h3 className="font-semibold text-lg mb-3 text-blue-600">
                        {medida.tipo_peca.toUpperCase()}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        {medida.busto && (
                          <div><strong>Busto:</strong> {medida.busto}cm</div>
                        )}
                        {medida.cintura && (
                          <div><strong>Cintura:</strong> {medida.cintura}cm</div>
                        )}
                        {medida.quadril && (
                          <div><strong>Quadril:</strong> {medida.quadril}cm</div>
                        )}
                        {medida.ombro && (
                          <div><strong>Ombro:</strong> {medida.ombro}cm</div>
                        )}
                        {medida.largura_costas && (
                          <div><strong>Larg. Costas:</strong> {medida.largura_costas}cm</div>
                        )}
                        {medida.cava_manga && (
                          <div><strong>Cava Manga:</strong> {medida.cava_manga}cm</div>
                        )}
                        {medida.grossura_braco && (
                          <div><strong>Gross. Braço:</strong> {medida.grossura_braco}cm</div>
                        )}
                        {medida.comprimento_manga && (
                          <div><strong>Comp. Manga:</strong> {medida.comprimento_manga}cm</div>
                        )}
                        {medida.cana_braco && (
                          <div><strong>Cana Braço:</strong> {medida.cana_braco}cm</div>
                        )}
                        {medida.alca && (
                          <div><strong>Alça:</strong> {medida.alca}cm</div>
                        )}
                        {medida.pescoco && (
                          <div><strong>Pescoço:</strong> {medida.pescoco}cm</div>
                        )}
                        {medida.comprimento && (
                          <div><strong>Comprimento:</strong> {medida.comprimento}cm</div>
                        )}
                        {medida.coxa && (
                          <div><strong>Coxa:</strong> {medida.coxa}cm</div>
                        )}
                        {medida.tornozelo && (
                          <div><strong>Tornozelo:</strong> {medida.tornozelo}cm</div>
                        )}
                        {medida.comprimento_calca && (
                          <div><strong>Comp. Calça:</strong> {medida.comprimento_calca}cm</div>
                        )}
                      </div>
                      
                      {medida.detalhes_superior && (
                        <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                          <strong>Detalhes Superiores:</strong> {medida.detalhes_superior}
                        </div>
                      )}
                      
                      {medida.detalhes_inferior && (
                        <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                          <strong>Detalhes Inferiores:</strong> {medida.detalhes_inferior}
                        </div>
                      )}
                      
                      {medida.observacoes && (
                        <div className="mt-3 p-2 bg-yellow-50 rounded text-sm">
                          <strong>Observações:</strong> {medida.observacoes}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instruções de Produção */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Instruções de Produção
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
              <h3 className="font-semibold text-lg mb-3 text-yellow-800">Checklist de Produção</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Verificar especificações técnicas antes de iniciar
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Confirmar materiais e insumos necessários
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Seguir cronograma de produção estabelecido
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Manter controle de qualidade durante o processo
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Comunicar eventuais problemas ou atrasos
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Finalizar com inspeção final de qualidade
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
